import type {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {useEffect, useLayoutEffect, useRef, useState, type RefObject} from 'react';

import {useHttpCalls} from '../api/useHttpCalls';
import {GridLifecycle} from '../engine/GridLifecycle';
import {useEagGridStore} from '../store/useEagGridStore';
import type {ActionsAlignment} from '../types/ActionsAlignment';
import type {BehaviorResponseBody} from '../types/api/settings';
import type {ExternalFilters} from '../types/ExternalFilters';
import type {QueryFilterObject} from '../types/QueryFilterObject';

type UseGridLifecycleParams = {
  gridCode: string;
  presetInstanceId?: string;
  behaviorOverrides?: Partial<BehaviorResponseBody>;
  disableAllActions?: boolean;
  hasSilentRefetching?: boolean;
  actionsAlignment?: ActionsAlignment;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  onApiError?: (error: FetchBaseQueryError) => void;
  predefinedColumns: readonly string[];
  onExternalFilterChange?: (filters: ExternalFilters) => void;
  onFilterChanged?: (filters: ExternalFilters) => void;
};

type UseGridLifecycleReturn = {
  refreshData: () => void;
  /** Retry a failed data/count fetch (forces the /count refetch). */
  retryFetch: () => void;
  queryIdRef: RefObject<string | undefined>;
  handleResetColumns: () => void;
};

/**
 * Thin React adapter over GridLifecycle. Creates the lifecycle once (with its engine),
 * pushes per-render volatile inputs every render, starts the store subscription on mount
 * (layout effect, before first paint), and disposes on unmount. Mirrors the live
 * queryId into a ref for row-action callbacks.
 *
 * Replaces the old `useServerData` + `useGridDataFetching` pair AND the
 * `useEagGridOrchestration` wrapper: the reactive fetch triggers, cold load, page-URL
 * sync, preset sync, host filter-change notification and column reset all live in
 * `GridLifecycle` now, not in effects here. `EagGridInner` calls this adapter directly.
 */
export const useGridLifecycle = (params: UseGridLifecycleParams): UseGridLifecycleReturn => {
  const store = useEagGridStore();
  // Data-fetch + preset-mutation http: deliberately built WITHOUT onApiError. /data, /count
  // and virtual-preset PUT failures must NOT route to the grid-level error screen — they
  // settle locally (fetch FSM) or are silent best-effort. Mirrors base, where useServerData
  // (`useHttpCalls({gridCode})`) and usePresetSync (`useHttpCalls()`) had no onApiError;
  // wiring it here was the regression that blanked the grid on a swallowable fetch error.
  const http = useHttpCalls({gridCode: params.gridCode});
  // Cold-load http: WITH onApiError + presetInstanceId so a fatal mount-phase failure
  // (presets/definition/dataQuery) surfaces the error screen. Mirrors the old
  // useEagGridDataLoader http; used ONLY by GridLifecycle.load().
  const loaderHttp = useHttpCalls({
    gridCode: params.gridCode,
    presetInstanceId: params.presetInstanceId,
    onApiError: params.onApiError,
    behaviorOverrides: params.behaviorOverrides,
  });

  const [lifecycle] = useState(
    () =>
      new GridLifecycle(
        {
          store,
          gridCode: params.gridCode,
          presetInstanceId: params.presetInstanceId,
          disableAllActions: params.disableAllActions,
          hasSilentRefetching: params.hasSilentRefetching,
          actionsAlignment: params.actionsAlignment,
        },
        {
          http,
          loaderHttp,
          queryModifier: params.queryModifier,
          predefinedColumns: params.predefinedColumns,
          onExternalFilterChange: params.onExternalFilterChange,
          onFilterChanged: params.onFilterChanged,
        }
      )
  );

  // --- Volatile-input syncing: push per-render inputs into the lifecycle ---
  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: no-deps effect pushes volatile inputs into the lifecycle every render (same role as the old useServerData engine.update)
  useEffect(function pushLifecycleInputs() {
    lifecycle.update({
      http,
      loaderHttp,
      queryModifier: params.queryModifier,
      predefinedColumns: params.predefinedColumns,
      onExternalFilterChange: params.onExternalFilterChange,
      onFilterChanged: params.onFilterChanged,
    });
  });

  // --- Lifecycle effects: startup/teardown ---
  // Layout effect starts the store subscription before first paint. start() itself
  // restores the page from the URL BEFORE the initial fetch (see GridLifecycle.start),
  // so the initial fetch uses the restored offset — a single fetch on mount.
  useLayoutEffect(
    function startLifecycle() {
      lifecycle.start();
    },
    [lifecycle]
  );

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: cleanup-only effect disposing the lifecycle (unsubscribe + abort) on unmount
  useEffect(
    function disposeLifecycle() {
      return () => lifecycle.dispose();
    },
    [lifecycle]
  );

  // --- Volatile-input syncing: mirror live store state into imperative refs ---
  const queryIdRef = useRef<string | undefined>(undefined);
  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: no-deps effect mirrors live queryId into a ref for row-action callbacks (same role as the old useGridDataFetching refs)
  useEffect(function syncImperativeRefs() {
    queryIdRef.current = store.getState().queryId || undefined;
  });

  return {
    refreshData: () => lifecycle.refresh(),
    retryFetch: () => lifecycle.retryFetch(),
    queryIdRef,
    handleResetColumns: () => lifecycle.resetColumns(),
  };
};
